import{ ReactNode } from "react";
import{ IconName } from "react-frontier";

export interface APIResponse<T = any>{
	error: boolean,
	code: string,
	message: string,
	data?: T &{ [x: string]: any },
}

export type SetLoading = (l: boolean) => void

export enum UserType{
	RESPONSABLE = 1,
	AUXILIAR = 2,
	COORDINADOR = 3,
	LIDER = 4
}

export enum MetadataType{
	INPUT = 1,
	TEXTAREA = 2,
	DROPDOWN = 20,
	MULTIPLE = 21,
	CHECKBOX = 40
}

export enum BankAccountOwnerType{
	FISICA = 1,
	MORAL = 2
}

export enum DepositType{
	SPEI = 1,
	CARD = 2
}

export enum AccountSelectionType{
	MY = 1,
	USER = 2,
	WORKER = 3
}

export enum EventStatusType{
	OPERATING = 1,
	PROMOTER_OPERATED = 2,
	POSTPONED = 3,
	CANCELLED = 4,
	NOT_OPERATED = 255
}

export enum EmploymentLogType{
	VACATIONS = 1,
	PERMISSION = 2,
	AUTHORIZED_REST = 3,
	DISABILITY = 4,
	ADJUSTMENT = 5,
}

export enum EmploymentScheduleType{
	WORK = 1,
	REST = 2,
}

export enum Department{
	OPERACIONES = 1,
	COMERCIAL = 2,
	DESARROLLO = 3,
	RECURSOS_HUMANOS = 4,
	MARKETING = 5,
	CONTABILIDAD = 6,
}

export interface UserClass{
	user_id: number,
	username: string,
	first_name: string,
	last_name: string,
	email: string,
	rfc?: string,
	account_rfc?: string,
	account_postal_code?: string,
	superadmin: boolean,
	tags: UserTag[],
	active: boolean,
	payout_type?: number,
	payout_type_name?: string,
	user_secret?: number,
	date_created: number,
	name?: string,
	access?: number[],
	events?: EventDateUser[],
	assets?: UserAsset[],
	notifications: number,
}

export interface EventDateUser{
	date_id: number,
	user_id: number,
	event_name: string,
	date: number,
	type_id: number,
	type_name: string,
	user_added: number,
	user_added_name?: string,
	date_added: number,
	date_created: number,
	user_full_name?: string,
}

export interface EventDate{
	date_id: number,
	event_name: string,
	date: number,
	status_id: number,
	closed: boolean,
	deleted: boolean,
	city: string,
	promoter: string,
	tickets_sold: number,
	tickets_free: number,
	venue_name: string,
	promoter_id?: number,
	location_id?: number,
	parent_date_id?: number,
	parent_event_name?: string,
	user_coordinator: number,
	user_coordinator_name: string,
	user_executive?: number,
	user_executive_name?: string,
	user_leader: number,
	user_leader_name: string,
	promoter_contact?: number,
	promoter_contact_name?: string,
	promoter_contact_phone?: string,
	users_count: number,
	date_created: number,
	date_deleted?: number,
	location: Location,
	users?: EventDateUser[],
	expenses: Expense[],
	budgets: Budget[],
	workers: EventWorker[],
	payouts: EventPayout[],
	related_events?: {
		date_id: number,
		event_name: string,
		date: number,
		tickets_sold: number,
		tickets_free: number,
	} [],
	requested_workers?: {
		request_id: number,
		title_id: number,
		title_name: string,
		quantity: number,
	} [],
	inventory_relations?: {
		request: boolean,
		shipment: boolean,
		request_id?: number,
		shipment_id?: number,
		approved?: boolean,
		rejected?: boolean,
		sent?: boolean,
	} [],
	issues?: Issue[]
}

export interface EventDateList{
	date_id: number,
	event_name: string,
	date: number,
	closed: boolean,
	status_id: EventStatusType,
	city: string,
	location_id: number,
	tickets_sold: number,
	tickets_free: number,
	promoter_id: number,
	promoter_name: string,
	user_coordinator: number,
	user_coordinator_name?: string,
	user_executive?: number,
	user_executive_name?: string,
	poster_url: string,
	venue_name: string,
	parent_date_id?: number,
	date_created: number,
}

export interface EventDateDays{
	day: number,
	month: number,
	year: number,
	events: number,
	pending: number,
}

export interface Expense{
	expense_id: number,
	expense_title: string,
	date_id?: number,
	type_id: number,
	type_name: string,
	account_id?: number,
	method_id?: number,
	method_name?: string,
	department_id?: number,
	department_name?: string,
	city?: string,
	amount: number,
	description?: string,
	approved: boolean,
	paid: boolean,
	deleted: boolean,
	rejected: boolean,
	reject_reason: number,
	reject_reason_name: string,
	user_creator?: number,
	user_creator_name?: string,
	user_paid?: number,
	user_paid_name?: string,
	user_approved?: number,
	user_approver_name?: string,
	user_deleted?: number,
	user_deleted_name?: string,
	user_rejected?: number,
	user_rejected_name?: string,
	date_created: number,
	date_paid?: number,
	date_deleted?: number,
	date_rejected?: number,
	account?: BankAccount,
	event_name?: string,
	assets?: Asset[],
}

export interface ExpenseForm{
	date_id?: number,
	type_id: number,
	department_id: number,
	method_id: number,
	expense_title: string,
	amount: number,
	description?: string,
	account_id: number,
	use_date_id?: boolean,
	is_reposition?: boolean,
	event_name?: string,
	account?: UserBankAccount,
}

export interface ExpenseType{
	type_id: number,
	type_name: string,
	description?: string,
	deleted: boolean,
}

export interface ExpenseRejectReason{
	reason_id: number,
	reason: string,
}

export interface PaymentMethod{
	method_id: number,
	method_name: string,
	deleted: boolean,
}

export interface ExpensesReportSummary{
	total_expenses: number,
	total_pending: number,
	total_approved: number,
	total_paid: number,
	total_rejected: number,
	amount_total: number,
	amount_pending: number,
	amount_approved: number,
	amount_paid: number,
	amount_rejected: number,
	total_events: number,
	total_coordinators: number,
	total_departments: number,
	total_types: number,
	total_replacements: number,
}

export interface EventsReportSummary{
	total_records: number,
	total_events: number,
	total_payouts: number,
	total_workers: number,
	total_coordinators: number,
	total_amount: number,
}

export interface UserBankAccount{
	account_id: number,
	name_account: string,
	is_primary?: boolean,
	rfc?: string,
	curp?: string,
	bank?: string,
	clabe: string,
	card_number: string,
	postal_code: string,
}

export interface BankAccount{
	account_id: number,
	name_account: string,
	rfc?: string,
	curp?: string,
	bank?: string,
	bank_participant?: number,
	clabe: string,
	card_number: string,
	postal_code: string,
	email_account?: string,
	date_created?: number,
}

export interface BankAccountForm{
	worker_id?: number,
	type: DepositType,
	email?: string,
	account_owner: string,
	clabe: string,
	card_number: string,
	bank_participant: number,
	rfc: string,
	postal_code: string,
	owner_type: BankAccountOwnerType,
}

export interface MetadataValue{
	value_id: number,
	metadata_id: number,
	value: string,
	deleted: boolean,
}

export interface BudgetForm{
	date_id: number,
	type_id: number,
	department_id: number,
	expense_id?: number,
	budget_title: string,
	date_deposit: number,
	amount: number,
	description?: string,
	event_name?: string,
	department_name?: string,
	use_date_id?: boolean,
}

export interface Budget{
	budget_id: number,
	date_id: number,
	event_name: string,
	city?: string,
	type_id: number,
	type_name: string,
	department_id?: number,
	department_name?: string,
	budget_title: string,
	date_deposit: number,
	amount: number,
	description?: string,
	approved: boolean,
	rejected: boolean,
	reject_reason: number,
	reject_reason_name?: string,
	deleted: boolean,
	expense_id?: number,
	account_id?: number,
	user_creator?: number,
	user_creator_name?: string,
	user_approved?: number,
	user_approver_name?: string,
	user_rejected?: number,
	user_rejected_name?: string,
	date_created: number,
	date_rejected?: number,
	date_deleted?: number,
	date_paid?: number,
	account?: BankAccount,
	requiere_date_id?: boolean,
	paid?: boolean,
	user_paid?: number,
	user_paid_name?: string,
}

export interface WorkerRating{
	rating: number,
	rating_comment: string,
	user_rated: number,
	user_rated_name: string,
	date_rated: number,
	date_id: number,
	event_name: string,
}

export interface ListWorker{
	worker_id: number,
	first_name: string,
	last_name: string,
	zipcode: string,
	city?: string,
	rfc?: string,
	onboarded?: boolean,
	valid_rfc?: boolean,
	cif_asset_id?: number,
	ban_reason?: number,
}

export interface Worker extends ListWorker{
	email: string,
	phone: string,
	rfc: string,
	curp: string,
	valid_rfc: boolean,
	comments?: string,
	cif_asset_id?: number,
	deleted: boolean,
	account_id?: number,
	user_creator: number,
	user_creator_name: string,
	user_deleted: number,
	user_deleted_name: string,
	date_created: number,
	date_deleted: number,
	rating?: number,
	image?: string,
	account?: BankAccount,
	events?: WorkerEventDate[],
	assets?: UserAsset[],
	ratings?: WorkerRating[],
	ban_reason?: number,
	ban_reason_name?: string,
	user_banned?: number,
	user_banned_name?: string,
	date_banned?: number,
}

export interface WorkerEventDate{
	date_id: number,
	event_name: string,
	title_id: number,
	title_name: string,
	description: string,
	payout_amount: number,
	cancelled: boolean,
	date_start: number,
	date_end: number,
	date: number,
	user_creator: number,
	user_creator_name: string,
	coordinator_name?: string,
	date_created: number,
	date_cancelled: number,
}

export interface WorkerForm{
	first_name: string,
	last_name: string,
	email: string,
	phone: string,
	rfc: string,
	curp: string,
	zipcode: string,
	comments?: string,
	city_name?: string,
	account_id?: number,
	have_bank?: boolean,
	account?: UserBankAccount,
}

export interface WorkerTitle{
	title_id: number,
	title_name: string,
	payout_amount: number,
	deleted: boolean,
	user_creator?: number,
	user_creator_name?: string,
	user_deleted?: number,
	user_deleted_name?: string,
	date_created: number,
	date_deleted: number,
}

export interface EventWorkerRequest{
	request_id: number,
	date_id: number,
	title_id: number,
	title_name: string,
	quantity: number,
	deleted: boolean,
	user_creator: number,
	user_creator_name: string,
	user_deleted?: number,
	user_deleted_name?: string,
	date_created: number,
	date_deleted?: number,
}

export interface EventWorker{
	date_id: number,
	worker_id: number,
	worker_name: string,
	title_id: number,
	title_name: string,
	description: string,
	payout_amount: number,
	phone?: string,
	confirmed?: boolean | number | null,
	rfc?: string,
	valid_rfc?: boolean,
	cancelled?: boolean,
	declined?: boolean | number,
	date_start?: number,
	date_end?: number,
	user_creator: number,
	user_creator_name: string,
	user_cancelled?: number,
	user_cancelled_name?: string,
	date_created: number,
	date_cancelled?: number,
	rating?: number,
	rating_comment?: string,
	user_rated?: number,
	user_rated_name?: string,
	date_rated?: number,
}

export interface EventPayout{
	payout_id: number,
	date_id?: number,
	event_name?: string,
	total_amount: number,
	paid_amount: number,
	pending_amount: number,
	worker_count: number,
	paid_count: number,
	completed: boolean,
	user_creator: number,
	user_creator_name: string,
	user_coordinator?: number,
	user_coordinator_name?: string,
	date_created: number,
	workers?: EventWorker[],
	payouts?: WorkerPayout[],
}

export interface WorkerPayout{
	payout_id: number,
	worker_id: number,
	worker_name: string,
	title_name: string,
	rfc: string,
	zipcode: string,
	bank: string,
	bank_participant?: any,
	clabe: string,
	card_number: string,
	amount: number,
	comment: string,
	paid: boolean,
	cancelled: boolean,
	user_creator: number,
	user_creator_name: string,
	user_cancelled: number,
   user_cancelled_name: string,
	user_paid: number,
	user_paid_name: string,
	date_paid: number,
	date_created: number,
}

export interface Asset{
	asset_id: number,
	type_id: number,
	asset_name: string,
	description: string,
	url: string,
	download_url?: string,
	reference?: string,
	deleted?: boolean,
	user_creator: number,
	user_creator_name?: string,
	user_deleted_name?: string,
	user_deleted?: number,
	date_created: number,
	date_deleted?: number,
}

export interface InventoryAsset extends Asset {
   item_asset_type?: number,
   item_asset_type_name?: string,
}

export interface UserAsset extends Asset{
	user_asset_type: number,
	user_asset_type_name: string,
	url: string
}

export interface Location{
	location_id?: number,
	name?: string,
	street: string,
	exterior_number: string,
	interior_number?: string,
	neighborhood: string,
	city: string,
	state: string,
	zipcode: string,
	country?: string,
	phone?: string,
	lattitude?: number,
	longitude?: number,
	place_id?: string,
	comments?: string,
}


export interface GooglePlacesResult{
	address_components?: {
		long_name: string,
		short_name: string,
		types: string[]
	}[],
	adr_address?: string,
	geometry?: {
		location?: {
			lat: ()=>number,
			lng: ()=>number,
		},
	},
	icon?: string,
	icon_background_color?: string,
	icon_mask_base_uri?: string,
	name?: string,
	place_id?: string,
	reference?: string,
	types?: string[],
	url?: string,
	utc_offset?: number,
	vicinity?: string,
}

export interface Breadcrumb{
	url: string,
	iconName?: IconName,
	sidebar: JSX.Element,
	text: string,
}

export interface SiteRoute{
	path: string,
	title: string | ((params: any)=>string),
	element: ReactNode,
	iconName?: IconName,
	sidebar?: JSX.Element,
	ignore?: boolean,
	children?: SiteRoute[],
	access?: number | number[],
}

export enum AssetType{
	IMAGE = 1,
	PDF = 2,
	VIDEO = 3,
}

export enum UserAssetType{
	ID = 1,
	ADDRESS = 2,
	CIF = 3,
	CURP = 4,
}

export enum InventoryItemAssetType{
	INVOICE = 1,
}

export enum InventoryItemStatus{
	AVAILABLE = 1,
	PENDING = 2,
	SHIPPED = 3,
	DELIVERED = 4,
	DELETED = 5,
}

export interface UserTag{
	tag_id: number,
	tag_name: string,
	tag_color: string,
}

export interface Metadata{
	metadata_id: number,
	metadata_name: string,
	metadata_comment: string,
	type_id: MetadataType,
	preadded: boolean,
	sort_order: number,
}

export interface EventDateMetadata{
	metadata_id: number,
	metadata_name: string,
	metadata_comment: string,
	type_id: MetadataType,
	value_id: number,
	value: string,
}

export interface Promoter{
	promoter_id: number,
	promoter_name: string,
	status_id: number,
	date_created: number,
	events_count?: number,
	events: EventDate [],
	contacts?: PromoterContact[],
}

export interface PromoterContact{
	contact_id: number,
	promoter_id?: number,
	contact_name: string,
	department: string,
	email: string,
	phone: string,
	deleted: boolean,
	user_creator?: number,
	user_creator_name?: string,
	user_deleted?: number,
	user_deleted_name?: string,
	date_created?: number,
	date_deleted?: number,
}

export interface AssetAddOptions{
	user_asset_type: number,
	user_asst_type_name: string,
	inventory_item_asset_type?: number,
	inventory_item_asset_type_name?: string,
}

export interface EventComment{
	comment_id: number,
	date_id: number,
	user_id: number,
	user_creator_name: string,
	content: string,
	date_created: number,
	date_deleted?: number,
	user_deleted?: number,
	user_deleted_name?: string,
}

export interface WorkerBanReason{
	reason_id: number,
	reason_name: string,
	deleted: boolean,
	user_creator: number,
	user_creator_name: string,
	user_deleted: number,
	user_deleted_name: string,
	date_created: number,
	date_deleted: number,
}

export interface Request{
	request_id: number,
	date_return: number,
	box_office_start_date?: number,
	description?: string,
	is_express?: boolean,
	deleted: boolean,
	rejected: boolean,
	rejection_reason?: string,
	approved: boolean,
	finalized: boolean,
	shipment_id?: number,
	user_creator: number,
	user_creator_name?: string,
	user_assigned?: number,
	user_assigned_name?: string,
	user_deleted?: number,
	user_rejected?: number,
	user_approver?: number,
	user_finalized?: number,
	date_created: number,
	date_deleted?: number,
	date_rejected?: number,
	date_approved?: number,
	date_finalized?: number,
	items?: {
		type_id: number,
		quantity: number,
		type_name?: string,
		type_icon?: string,
	}[],
	events?: EventDate[]
}

export interface ShippingCarrier{
	carrier_id: number,
	carrier_name: string,
}

export interface ShipmentItem{
	item_id: number,
	type_id: number,
	type_name?: string,
	quantity: number,
}

export interface Shipment{
	shipment_id: number,
	parent_shipment_id?: number,
	request_id?: number,
	warehouse_id?: number,
	warehouse_name?: string,
	destination_id?: number,
	destination?: Location,
	destination_city?: string,
	description?: string,
	date_return?: number,
	box_office_start_date?: number,
	deleted: boolean,
	user_creator: number,
	user_creator_name?: string,
	user_assigned?: number,
	user_assigned_name?: string,
	user_deleted?: number,
	user_deleted_name?: string,
	date_created: number,
	date_deleted?: number,
	item_count?: number,
	has_children?: boolean,
	shipped?: boolean,
	delivered?: boolean,
	items?: ShipmentItemDetail[],
	events?: { date_id: number, event_name: string, date: number }[],
}

export interface ShipmentItemDetail{
	shipment_id: number,
	item_id: number,
	item_name?: string,
	type_id?: number,
	type_name?: string,
	type_icon?: IconName,
	serial_number?: string,
	tracking_number?: string,
	carrier_id?: number,
	carrier_name?: string,
	user_recipient?: number,
	user_recipient_name?: string,
	recipient_name?: string,
	recipient_contact?: string,
	shipped: boolean,
	delivered: boolean,
	deleted: boolean,
	user_creator: number,
	user_creator_name?: string,
	user_deleted?: number,
	date_created: number,
	date_shipped?: number,
	date_delivered?: number,
	date_deleted?: number,
	warehouse_name?: string,
	location_city?: string,
}

export interface AvailableItemForShipment{
	item_id: number,
	item_name?: string,
	type_id: number,
	type_name: string,
	type_icon?: IconName,
	serial_number?: string,
	model?: string,
	warehouse_id?: number,
	warehouse_name?: string,
	location_city?: string,
	distance?: number,
}

export interface InventoryType{
   type_id: number,
   type_name: string,
   type_icon?: IconName,
   consumable: number,
   deleted: number,
   user_creator: number,
   user_creator_name?: string,
   user_deleted?: number,
   user_deleted_name?: string,
   date_created: number,
   date_deleted?: number,
}

export interface InventoryWarehouse{
   warehouse_id: number,
   warehouse_name: string,
   location_id: number,
   location?: Location;
   location_city?: string,
	capacity: number,
   description?: string,
   deleted?: number,
   user_creator?: number,
   user_creator_name?: string,
   user_deleted?: number,
   user_deleted_name?: string,
   date_created?: number,
   date_deleted?: number,
   items?: any[],
   total_items?: number,
   briefcase_items?: number,
}

export interface InventoryItem{
   item_id: number,
   type_id: number,
	item_name?: string,
	description?: string,
	serial_number?: string,
	model?: string,
	seller_name?: string,
	date_purchase?: number,
   deleted?: number,
   user_creator?: number,
   user_creator_name?: string,
   user_deleted?: number,
   user_deleted_name?: string,
   date_created?: number,
   date_deleted?: number,
	user_assigned?: number,
   user_assigned_name?: string,
   type_name?: string,
   type_icon?: IconName,
   consumable?: number,
   log_id?: number,
   warehouse_id?: number,
   warehouse_name?: string,
   location_id?: number,
   location_name?: string,
	location_city?: string,
   last_moved?: number,
	shipment_id?: number,
	status?: InventoryItemStatus,
	assets?: InventoryAsset[],
	logs?: InventoryItemLog[],
}

export interface GroupedItemsByType{
   type_id: number;
   type_name: string;
   type_icon: IconName;
   consumable: number;
   item_count: number;
}

export interface InventoryItemLog{
   log_id: number,
   item_id: number,
   shipment_id?: number,
   shipment_description?: string,
   warehouse_id?: number,
   warehouse_name?: string,
	location_id?: number,
	location?: Location,
	user_assigned?: number,
   user_assigned_name?: string,
   user_creator?: number,
   user_creator_name?: string,
   date_created: number,
}

export interface Employment{
	employment_id: number,
	employment_name: string,
	date_start: number,
	date_end?: number,
	location_id?: number,
	location?: Location,
	allow_remote: boolean,
	geofence_radius: number,
	late_seconds: number,
	penalty_seconds: number,
	city?: string,
	country?: string,
	user_creator: number,
	user_creator_name: string,
	date_created: number,
	user_deleted?: number,
	user_deleted_name?: string,
	date_deleted?: number,
	deleted: boolean,
	employee_count?: number,
	schedules?: EmploymentSchedule[],
}

export interface EmploymentSchedule{
	schedule_id: number,
	employment_id: number,
	employment_name?: string,
	employees?: number,
	type_id?: number,
	day: number,
	time_start: number,
	time_end: number,
	salary: number,
	deleted: boolean,
	user_creator: number,
	user_creator_name: string,
	date_created: number,
	user_deleted?: number,
	user_deleted_name?: string,
	date_deleted?: number,
	workers?: UserEmployment[],
}

export interface UserEmployment{
	user_id: number,
	first_name?: string,
	last_name?: string,
	name: string,
	email?: string,
	phone?: string,
	rfc?: string,
	curp?: string,
	employment_id?: number,
	employment_name?: string,
	allow_remote?: boolean,
	geofence_radius?: number,
	late_seconds?: number,
	penalty_seconds?: number,
	type_id?: number,
	schedule_id?: number,
	schedule_ids?: number[],
	schedule_type_ids?: number[],
	day?: number,
	days?: number[],
	time_start?: number,
	time_end?: number,
	date_start?: number,
	date_end?: number,
	log_date_start?: number,
	log_date_end?: number,
	salary?: number,
	deleted?: boolean,
	user_creator?: number,
	user_creator_name?: string,
	user_deleted?: number,
	date_created?: number,
	date_deleted?: number,
	incidences?: EmploymentIncidenceSummary[],
	log_id?: number,
	logs?: UserEmploymentLog[],
	log_type_id?: number,
	log_type_name?: string,
	notes?: string,
}

export interface EmploymentIncidenceSummary{
	log_id?: number,
	user_id: number,
	schedule_id: number,
	log_type_id: number,
	log_type_name?: string,
	notes?: string,
	day: number,
	time_start: number,
	time_end: number,
	date_start: number,
}

export interface UserEmploymentLog{
	log_id: number,
	user_id: number,
	name?: string,
	schedule_id: number,
	type_id?: number,
	log_type_id?: number,
	log_type_name?: string,
	notes?: string,
	employment_id?: number,
	employment_name?: string,
	day?: number,
	time_start?: number,
	time_end?: number,
	salary: number,
	date_start: number,
	date_end?: number,
	start_longitude: number,
	start_lattitude: number,
	end_longitude?: number,
	end_lattitude?: number,
	paid?: boolean,
	approved?: boolean,
	deleted: boolean,
	user_creator: number,
	user_approved?: number,
	user_paid?: number,
	user_deleted?: number,
	date_created: number,
	date_approved?: number,
	date_paid?: number,
	date_deleted?: number,
	work_date?: string,
	day_of_week?: number,
	worked_minutes?: number,
}

export interface UserAssistance{
	log_id: number,
	user_id: number,
	name: string,
	schedule_id: number,
	type_id?: number,
	log_type_id?: number,
	log_type_name?: string,
	notes?: string,
	day: number,
	time_start: number,
	time_end: number,
	total_time: number,
	salary: number,
	date_start: number,
	date_end?: number,
	late_seconds: number,
	penalty_seconds: number,
	employment_id: number,
	employment_name: string,
	date_created: number,
}

export interface EmploymentScheduleDetail{
	schedule_id: number,
	employment_id: number,
	employment_name: string,
	employees?: number,
	day: number,
	time_start: number,
	time_end: number,
	salary: number,
	deleted: boolean,
	user_creator: number,
	user_creator_name: string,
	date_created: number,
	user_deleted?: number,
	date_deleted?: number,
	workers: UserEmployment[],
}

export interface RFCWorker{
	worker_id: number,
	first_name: string,
	last_name: string,
	rfc: string,
	curp: string,
	zipcode: string,
	date_created: number,
}

export interface Contract{
	contract_id: number,
	contract_title: string,
	content: string,
}

export interface ConfirmationData{
	event_name: string,
	date: number,
	venue_name: string,
	city: string,
	poster_url: string,
	worker_name: string,
	confirmed: number,
	declined: number,
	payout_amount: number,
	title_name: string,
	contract_content: string,
}

export interface Issue {
   issue_id: number;
   title: string;
   content: string;
   issue_types: IssueType[];
   status: {
		status_id: number;
		status_name: string;
	};
   priority: number;
   tag: {
      tag_id: number;
      tag_name: string;
   };
   users: UserClass[];
   comments_amount?: number;
   date_created?: number;
	events?: EventDate[];
	date_schedule: number;
	user_creator: UserClass;
}

export interface IssueType{
   type_id: number;
   type_name: string;
   type_icon: string;
}

export enum IssuesStatus{
   PENDING = 1,
   IN_PROGRESS = 2,
   ON_HOLD = 3,
   RESOLVED = 4,
   CANCELLED = 255,
	CONFIRMED = 5
}

export interface IssueComment {
   comment_id: number;
   issue_id: number;
   user_id: number;
   user_creator_name: string;
   user_name?: string;
   content: string;
   date_created: number;
   deleted?: boolean | number;
   user_deleted?: number;
   user_deleted_name?: string;
   date_deleted?: number;
}

export{};
